import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig, effect, importProvidersFrom, inject, isDevMode, provideAppInitializer,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';

import {
  AuthService, ENVIRONMENT, HELP_HUB_SECTIONS, LanguageService, authInterceptor,
} from '@instaclause-webapp/shared-core';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import {
  TimeagoCustomFormatter, TimeagoFormatter, TimeagoIntl, TimeagoModule,
} from 'ngx-timeago';

import { environment } from '../environments/environment';

import { appRoutes } from './app.routes';
import { apiBaseUrl, LanguagesClient, MeClient } from './core/api/api.service';
import { docsBaseUrl } from './core/doc-generator/api.service';
import { ACCOUNTANT_HELP_HUB_SECTIONS } from './core/help-hub-sections';
import { integrationsBaseUrl } from './core/integrations/api.service';
import { MeService } from './core/me.service';
import { TranslocoHttpLoader } from './transloco-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withComponentInputBinding()
    ),
    provideHttpClient(
      withInterceptors([ authInterceptor ]),
    ),
    provideTransloco({
      config: {
        availableLangs: [ 'en', 'nl', 'be-nl', 'fr' ],
        defaultLang: 'en',
        fallbackLang: 'nl',
        missingHandler: { useFallbackTranslation: true },
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoPersistLang({
      storage: {
        useValue: localStorage,
      },
    }),
    importProvidersFrom(
      TimeagoModule.forRoot({
        intl: {
          provide: TimeagoIntl,
        },
        formatter: {
          provide: TimeagoFormatter,
          useClass: TimeagoCustomFormatter,
        },
      }),
    ),
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: apiBaseUrl,
      useValue: environment.apiBaseUrl,
    },
    {
      provide: docsBaseUrl,
      useValue: environment.docsBaseUrl,
    },
    {
      provide: HELP_HUB_SECTIONS,
      useValue: ACCOUNTANT_HELP_HUB_SECTIONS,
    },
    {
      provide: integrationsBaseUrl,
      useValue: environment.integrationsBaseUrl,
    },
    LanguagesClient,
    MeClient,
    // Starts loading the customer context on app load;
    provideAppInitializer(() => {
      const meService = inject(MeService);
      const languageService = inject(LanguageService);

      inject(AuthService).login$.subscribe(() => languageService.resetToOfficeLanguage());
      effect(() => languageService.setOfficeCountry(meService.countryCode()));
    }),
  ],
};
